import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";

import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import MultipleSelector, { Option } from "@/shared/ui/multiple-selector";
import { Skeleton } from "@/shared/ui/skeleton";
import { useState, useEffect, useMemo } from "react";
import { Pack } from "../services/packs/types";
import { RenderPercentageTable } from "./RenderPercentageTable";
import { Select as CustomSelect, ReactSelect } from "@/shared/widgets";
import { TagsService, Tag } from "@/features/tags/service/tagService";
import CreatorService from "@/features/creators/services/creatorService";

interface Creator {
  id: string;
  name: string;
}

interface CreatorWithPercentage {
  id: string;
  name: string;
  percentage: number;
  displayOrder: number;
}

interface FormFieldsProps {
  defaultValues?: Partial<Pack>;
  errors?: Record<string, string>;
  onCreatorsChange?: (creators: CreatorWithPercentage[]) => void;
  clearFieldError?: (field: string) => void;
  onValuesChange?: (values: { selectedCreator: string; selectedTags: string[] }) => void;
  readOnly?: boolean;
}

export const FormFields = ({
  defaultValues,
  errors = {},
  onCreatorsChange,
  clearFieldError,
  onValuesChange,
  readOnly = false,
}: FormFieldsProps) => {
  const { data: creators = [], isLoading: isLoadingCreators } = useCustomQuery({
    serviceInstance: CreatorService.Instance,
    selectAction: (service) => service.getCreators,
    parameter: undefined,
  }) as { data: Creator[]; isLoading: boolean };

  const [selectedCreator, setSelectedCreator] = useState<string>(defaultValues?.packsCreators?.[0]?.creatorId || "");
  const [selectedCollaborators, setSelectedCollaborators] = useState<Option[]>([]);
  const [creatorsWithPercentage, setCreatorsWithPercentage] = useState<CreatorWithPercentage[]>([]);
  const [selectedTags, setSelectedTags] = useState<{ value: string; label: string; description?: string }[]>([]);
  const [tagSearchQuery, setTagSearchQuery] = useState<string>("");

  // Custom query para buscar tags
  const { data: tagsResponse, isLoading: isLoadingTags } = useCustomQuery({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.getTags,
    parameter: tagSearchQuery.length >= 2 ? { search: tagSearchQuery, limit: 20 } : undefined,
  }) as { data: { data: Tag[]; page: number; limit: number; totalPage: number; totalItems: number } | undefined; isLoading: boolean };

  // Mutations para vincular/desvincular tags
  const linkTagMutation = useCustomMutation({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.linkTagToPack,
  });

  const unlinkTagMutation = useCustomMutation({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.unlinkTagFromPack,
  });

  // Converte os dados das tags para o formato do ReactSelect
  const tagOptions = useMemo(() => {
    const tagsData = tagsResponse?.data || [];
    return tagsData.map((tag: Tag) => ({
      value: tag.id,
      label: tag.translations.find(t => t.languageCode === 'PT_BR')?.name || tag.translations[0]?.name || tag.id,
      // description: tag.isCategory ? 'Categoria' : 'Tag',
      // icon: <TagIcon className="h-4 w-4" />
    }));
  }, [tagsResponse]);

  // Função para buscar tags (atualiza o query)
  const searchTags = (query: string) => {
    setTagSearchQuery(query);
  };

  // Função para gerenciar mudanças nas tags
  const handleTagsChange = async (newValue: string | string[]) => {
    if (readOnly) return;

    const newTagValues = Array.isArray(newValue) ? newValue : [];
    const currentTagValues = selectedTags.map(tag => tag.value);

    // Tags adicionadas (novas seleções)
    const addedTags = newTagValues.filter(tagId => !currentTagValues.includes(tagId));

    // Tags removidas (seleções removidas)
    const removedTags = currentTagValues.filter(tagId => !newTagValues.includes(tagId));

    // Atualiza o estado local primeiro
    const formattedTags = newTagValues.map(value => ({
      value,
      label: tagOptions.find(opt => opt.value === value)?.label || value
    }));
    setSelectedTags(formattedTags);

    // Notifica mudanças nas tags selecionadas
    if (onValuesChange) {
      onValuesChange({
        selectedCreator: selectedCreator,
        selectedTags: newTagValues
      });
    }

    // Se o pack já existe, vincula/desvincula tags imediatamente
    if (defaultValues?.id) {
      // Vincula novas tags
      for (const tagId of addedTags) {
        try {
          await linkTagMutation.mutateAsync({
            tagId,
            packId: defaultValues.id
          });
        } catch (error) {
          console.error(`Erro ao vincular tag ${tagId}:`, error);
        }
      }

      // Desvincula tags removidas
      for (const tagId of removedTags) {
        try {
          await unlinkTagMutation.mutateAsync({
            tagId,
            packId: defaultValues.id
          });
        } catch (error) {
          console.error(`Erro ao desvincular tag ${tagId}:`, error);
        }
      }
    }
    // Se o pack não existe ainda (criação), apenas atualiza o estado local
    // As tags serão vinculadas após criar o pack
  };

  // Inicializa tags selecionadas com valores padrão
  useEffect(() => {
    if (defaultValues?.packsTags && defaultValues.packsTags.length > 0) {
      // Usa packsTags para preencher as tags selecionadas
      const formattedTags = defaultValues.packsTags
        .filter(packTag => !packTag.deleted) // Filtra tags não deletadas
        .map(packTag => ({
          value: packTag.tagId,
          label: packTag.tag.translations.find(t => t.languageCode === 'PT_BR')?.name ||
            packTag.tag.translations[0]?.name ||
            packTag.tagId
        }));

      setSelectedTags(formattedTags);
    } else if (defaultValues?.tags) {
      // Fallback para o campo tags antigo (compatibilidade)
      const tagsArray = Array.isArray(defaultValues.tags)
        ? defaultValues.tags
        : defaultValues.tags.split(',').map(tag => tag.trim()).filter(Boolean);

      const formattedTags = tagsArray.map(tag => ({
        value: tag,
        label: tag
      }));

      setSelectedTags(formattedTags);
    }
  }, [defaultValues?.packsTags, defaultValues?.tags]);

  // Notifica mudanças nos valores
  useEffect(() => {
    onValuesChange?.({
      selectedCreator,
      selectedTags: selectedTags.map(tag => tag.value)
    });
  }, [selectedCreator, selectedTags, onValuesChange]);


  useEffect(() => {
    if (defaultValues?.packsCreators && defaultValues.packsCreators.length > 0) {
      // Ordena os criadores por displayOrder
      const sortedCreators = [...defaultValues.packsCreators].sort((a, b) =>
        (a.displayOrder || 0) - (b.displayOrder || 0)
      );

      // Define o criador principal (primeiro da lista ordenada)
      const mainCreator = sortedCreators[0];
      if (mainCreator) {
        setSelectedCreator(mainCreator.id);

        // Define os colaboradores (resto da lista)
        const collaborators = sortedCreators.slice(1).map(creator => {
          // Tenta encontrar o nome do criador na lista de criadores disponíveis
          const creatorInfo = creators.find(c => c.id === creator.id);
          return {
            label: creatorInfo?.name || `Creator ${creator.id}`,
            value: creator.id
          };
        });
        setSelectedCollaborators(collaborators);

        // Atualiza a tabela com porcentagens e displayOrder corretos
        setCreatorsWithPercentage(sortedCreators.map(creator => {
          // Tenta encontrar o nome do criador na lista de criadores disponíveis
          const creatorInfo = creators.find(c => c.id === creator.id);
          return {
            id: creator.id,
            name: creatorInfo?.name || `Creator ${creator.id}`,
            percentage: creator.percentage,
            displayOrder: creator.displayOrder || 0
          };
        }));
      }
    }
  }, [defaultValues?.packsCreators, creators]);

  // Função para atualizar a tabela de criadores
  const updateCreatorsTable = () => {
    const newCreators: CreatorWithPercentage[] = [];

    // Mapeia os criadores existentes para manter as porcentagens
    const existingCreators = creatorsWithPercentage.reduce<Record<string, CreatorWithPercentage>>((acc, creator) => {
      acc[creator.id] = creator;
      return acc;
    }, {});

    const usedIds = new Set<string>();
    let displayOrder = 1;

    // Adiciona o criador principal
    if (selectedCreator) {
      const creatorData = creators.find(c => c.id === selectedCreator);
      const existingCreator = existingCreators[selectedCreator];

      if (creatorData) {
        newCreators.push({
          id: creatorData.id,
          name: creatorData.name,
          percentage: existingCreator?.percentage || 100,
          displayOrder: displayOrder++,
        });
        usedIds.add(creatorData.id);
      }
    }


    // Adiciona os colaboradores existentes que ainda não foram adicionados
    selectedCollaborators.forEach((collaborator) => {
      if (usedIds.has(collaborator.value)) return;

      const collaboratorData = creators.find((c) => c.id === collaborator.value);
      if (collaboratorData) {
        const existingCollaborator = existingCreators[collaboratorData.id];
        newCreators.push({
          id: collaboratorData.id,
          name: collaboratorData.name,
          percentage: existingCollaborator?.percentage ?? 100,
          displayOrder: displayOrder++,
        });
        usedIds.add(collaboratorData.id);
      }
    });

    // Adiciona quaisquer criadores existentes que não foram incluídos ainda
    creatorsWithPercentage.forEach(creator => {
      if (!usedIds.has(creator.id)) {
        const creatorData = creators.find(c => c.id === creator.id);
        if (creatorData) {
          newCreators.push({
            ...creator,
            displayOrder: displayOrder++
          });
          usedIds.add(creator.id);
        }
      }
    });

    // Ordena por displayOrder para garantir a ordem correta
    const sortedCreators = [...newCreators].sort((a, b) =>
      (a.displayOrder || 0) - (b.displayOrder || 0)
    );

    // Atualiza os displayOrders para garantir que estejam em sequência
    const creatorsWithUpdatedOrder = sortedCreators.map((creator, index) => ({
      ...creator,
      displayOrder: index + 1
    }));

    setCreatorsWithPercentage(creatorsWithUpdatedOrder);

    // Notifica o componente pai sobre a mudança nos criadores
    if (onCreatorsChange) {
      onCreatorsChange(creatorsWithUpdatedOrder);
    }
  };


  const handleCreatorChange = (newCreatorId: string) => {
    if (readOnly) return;
    clearFieldError?.("creator");
    setSelectedCreator(newCreatorId);
    onValuesChange?.({
      selectedCreator: newCreatorId,
      selectedTags: selectedTags.map(tag => tag.value)
    });
    setSelectedCollaborators((prev) =>
      prev.filter((collaborator) => collaborator.value !== newCreatorId)
    );

    setCreatorsWithPercentage((prev) => {
      // Remove o criador anterior e o novo (caso esteja como colaborador também)
      const filtered = prev.filter((creator) => creator.id !== selectedCreator && creator.id !== newCreatorId);

      const creatorData = creators.find((c) => c.id === newCreatorId);
      if (!creatorData) return filtered;

      return [
        {
          id: creatorData.id,
          name: creatorData.name,
          percentage: 100,
          displayOrder: 1,
        },
        ...filtered.map((creator, i) => ({
          ...creator,
          displayOrder: i + 2,
        })),
      ];
    });

    setSelectedCreator(newCreatorId);
  };


  // Atualiza quando colaboradores mudam
  const handleCollaboratorsChange = (newCollaborators: Option[]) => {
    if (readOnly) return;
    setSelectedCollaborators(newCollaborators);

    setCreatorsWithPercentage((prev) => {
      const existing = new Set(prev.map((p) => p.id));
      const base: CreatorWithPercentage[] = [];

      // Adiciona o criador principal
      const mainCreator = creators.find((c) => c.id === selectedCreator);
      if (mainCreator) {
        base.push({
          id: mainCreator.id,
          name: mainCreator.name,
          percentage: prev.find(p => p.id === mainCreator.id)?.percentage || 100,
          displayOrder: 1,
        });
      }

      // Adiciona os colaboradores (exceto se forem o criador)
      let displayOrder = 2;
      for (const collab of newCollaborators) {
        if (collab.value === selectedCreator) continue;

        const collabData = creators.find((c) => c.id === collab.value);
        if (!collabData) continue;

        base.push({
          id: collabData.id,
          name: collabData.name,
          percentage: prev.find(p => p.id === collabData.id)?.percentage || 100,
          displayOrder: displayOrder++,
        });
      }

      return base;
    });
  };


  const updatePercentage = (creatorId: string, newPercentage: number) => {
    if (readOnly) return;
    setCreatorsWithPercentage(prev =>
      prev.map(creator =>
        creator.id === creatorId
          ? { ...creator, percentage: Math.max(0, Math.min(100, newPercentage)) }
          : creator
      )
    );
  };

  const totalPercentage = creatorsWithPercentage.reduce((sum, creator) => sum + creator.percentage, 0);

  // Notifica o componente pai sobre mudanças nos criadores
  useEffect(() => {
    onCreatorsChange?.(creatorsWithPercentage);
  }, [creatorsWithPercentage, onCreatorsChange]);

  useEffect(() => {
    updateCreatorsTable();
  }, [selectedCreator, selectedCollaborators]);

  const collaborators = useMemo(() => {
    return creators
      .filter((c) => c.id !== selectedCreator)
      .map((e) => ({
        label: e.name,
        value: e.id,
      }));
  }, [creators, selectedCreator]);

  useEffect(() => {
    const creatorsData = defaultValues?.packsCreators?.map((creator) => creators.find((c) => c.id === creator.creatorId));

    setSelectedCreator(creatorsData?.[0]?.id || "");

    setSelectedCollaborators(
      creatorsData?.slice(1).map((creator) => ({
        label: creator?.name,
        value: creator?.id,
      })) || []
    );
    setCreatorsWithPercentage(defaultValues?.packsCreators?.map((creator) => ({
      id: creator.creatorId,
      name: creators.find((c) => c.id === creator.creatorId)?.name || "",
      percentage: creator.percentage,
      displayOrder: creator.displayOrder,
    })) || []);

  }, [creators, defaultValues]);
  return (
    <>
      <div className="space-y-2">
        <Label className="text-white" htmlFor="name">
          Nome
        </Label>
        <Input
          id="name"
          name="name"
          placeholder="Digite o nome do pack"
          defaultValue={defaultValues?.name || ""}
          readOnly={readOnly}
          aria-readonly={readOnly}
          className={`bg-white/[0.03] border-white/[0.06] text-white ${errors.name ? "border-red-500" : ""
            }`}
          onChange={() => clearFieldError?.("name")}
        />
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-white" htmlFor="description">
          Descrição
        </Label>
        <Textarea
          id="description"
          name="description"
          placeholder="Digite a descrição do pack"
          defaultValue={defaultValues?.translations?.[0]?.description || ""}
          readOnly={readOnly}
          aria-readonly={readOnly}
          className={`bg-white/[0.03] border-white/[0.06] text-white min-h-[100px] ${errors.description ? "border-red-500" : ""
            }`}
          onChange={() => clearFieldError?.("description")}
        />
        {errors.description && (
          <p className="text-red-500 text-sm mt-1">{errors.description}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label className="text-white" htmlFor="tags">
          Tags
        </Label>
        {/* ReactSelect customizado com busca assíncrona para tags */}
        <ReactSelect
          options={tagOptions}
          value={selectedTags.map(tag => tag.value)}
          onChange={handleTagsChange}
          multiple
          searchable
          onSearch={searchTags}
          loading={isLoadingTags || linkTagMutation.isPending || unlinkTagMutation.isPending}
          placeholder="Digite para buscar tags..."
          noOptionsMessage={isLoadingTags ? "Buscando tags..." : "Digite pelo menos 2 caracteres para buscar"}
          minSearchLength={2}
          error={!!errors.tags}
          errorMessage={errors.tags}
          disabled={readOnly}
        />
        {/* Campo hidden para manter compatibilidade com o formulário */}
        <input
          type="hidden"
          name="tags"
          value={selectedTags.map(tag => tag.value).join(',')}
        />
        {errors.tags && (
          <p className="text-red-500 text-sm mt-1">{errors.tags}</p>
        )}
      </div>

      <div className="flex flex-row gap-6">
        <div className="flex-1">
          <div className="mb-2">
            <Label className="text-white" htmlFor="creator">
              Criador
            </Label>
          </div>
          <CustomSelect
            value={selectedCreator}
            onChange={handleCreatorChange}
            data={creators.map((creator: { id: string, name: string, avatar: string }) => ({
              value: creator.id,
              label: creator.name,
              image: creator.avatar,
            })) || []}
            placeholder="Selecione um criador"
            disabled={readOnly}
          />
          {errors.creator && (
            <p className="text-red-500 text-sm mt-1">{errors.creator}</p>
          )}
        </div>

        <div className="flex-1">
          <div className="mb-2">
            <Label className="text-white">
              Colaboradores
            </Label>
          </div>
          {isLoadingCreators ? (
            <Skeleton className="flex min-h-10 border-white/10 bg-[#212121]" />
          ) : (
            <>
              <MultipleSelector
                value={selectedCollaborators}
                onChange={handleCollaboratorsChange}
                hidePlaceholderWhenSelected
                disabled={!selectedCreator || readOnly}
                placeholder="Selecione os colaboradores"
                className="flex min-h-10 border-white/10 bg-[#212121]"
                hideClearAllButton
                defaultOptions={collaborators}
                loadingIndicator={isLoadingCreators ? "Carregando..." : undefined}
                emptyIndicator={
                  <span className="text-white">
                    Nenhum colaborador encontrado
                  </span>
                }
                badgeClassName="rounded-sm text-normal text-gray-300 border border-gray-100 bg-gray-300/20 hover:bg-gray-600/50"
              />
            </>

          )}
          {errors.collaborators && (
            <p className="text-red-500 text-sm mt-1">{errors.collaborators}</p>
          )}
        </div>
      </div>


      <RenderPercentageTable
        creators={creatorsWithPercentage}
        onUpdatePercentage={updatePercentage}
        readOnly={readOnly}
      />
    </>
  );
};
